import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Grid,
  Divider,
  alpha,
  useTheme,
  TextField,
  Autocomplete,
  Stack,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import useAxiosPrivate from "app/hooks/useAxiosPrivate";
import useAPINew from "app/hooks/useAPINew";
import { Formik, Field } from "formik";
import * as yup from "yup";
import moment from "moment";
import { DateField, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { useToaster, Message } from "rsuite";

const phoneRegExp = /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

const validationSchema = yup.object({
  Name: yup.string().required("Name is required"),
  MobileNo: yup.string().matches(phoneRegExp, "Phone number is not valid").nullable(true),
  Relation: yup.object().shape({
    title: yup.string().required("Relation is required"),
  }).nullable(),
});

export default function AddFamilyMemberDialog({ open, onClose, referenceId, onSuccess }) {
  const theme = useTheme();
  const axiosPrivate = useAxiosPrivate();
  const toaster = useToaster();

  const ProfessionList = useAPINew("GetProfessionList.php");
  const WorkTypeList = useAPINew("GetWorkTypeList.php");
  const FamilyList = useAPINew("GetFamilyTypeList.php");
  const SalutationList = useAPINew("GetSalutationList.php");

  const handleSubmit = (values, { setSubmitting, resetForm }) => {
    const newData = {
      ...values,
      DOBR: values.DOBR ? moment(values.DOBR).format("YYYY-MM-DD") : null,
      DOBG: values.DOBG ? moment(values.DOBG).format("YYYY-MM-DD") : null,
      DOM: values.DOM ? moment(values.DOM).format("YYYY-MM-DD") : null,
      Work: values.Work?.value || null,
      WorkType: values.WorkType?.value || null,
      Relation: values.Relation?.value || null,
      MaritalStatus: values.MaritalStatus?.value || null,
      Salutation: values.Salutation?.value || null,
      ReferenceID: referenceId,
    };

    axiosPrivate
      .post("NewFamilyMember.php", newData)
      .then((response) => {
        const result = response.data;
        if (result.status === 1) {
          toaster.push(
            <Message type="success" bordered showIcon>
              <strong>Success!</strong> {result.Success}
            </Message>,
            { placement: "topEnd", duration: 3000 }
          );
          if (onSuccess) {
            onSuccess(result.ID); // Assuming result.ID is the new family member's ID
          }
          resetForm();
          onClose();
        } else {
          toaster.push(
            <Message type="error" bordered showIcon>
              <strong>Error!</strong> {result.Error}
            </Message>,
            { placement: "topEnd", duration: 3000 }
          );
        }
      })
      .catch((error) => {
        console.error("Error submitting data:", error);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth disableEnforceFocus PaperProps={{
      sx: {
        borderRadius: 3,
        background: alpha(theme.palette.background.paper, 0.95),
        backdropFilter: "blur(10px)",
        boxShadow: theme.shadows[10],
      }
    }}>
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" fontWeight="bold">Add Family Member</Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: theme.palette.grey[500] }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 3 }}>
        <Formik
          initialValues={{
            Salutation: null,
            Name: "",
            DOBR: null,
            DOBG: null,
            DOM: null,
            MaritalStatus: null,
            Gender: "",
            Work: null,
            WorkType: null,
            Education: "",
            WorkPlace: "",
            OldLIC: "",
            MobileNo: "",
            Relation: null,
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
            isSubmitting,
          }) => (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    name="Salutation"
                    options={SalutationList.data || []}
                    getOptionLabel={(option) => option?.title || ""}
                    value={values.Salutation}
                    onChange={(_, newValue) => setFieldValue("Salutation", newValue)}
                    renderInput={(params) => (
                      <TextField {...params} label="Salutation" variant="standard"
                        error={touched.Salutation && Boolean(errors.Salutation)}
                        helperText={touched.Salutation && errors.Salutation}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    name="Name"
                    label="Name"
                    variant="standard"
                    value={values.Name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.Name && Boolean(errors.Name)}
                    helperText={touched.Name && errors.Name}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    name="Relation"
                    options={FamilyList.data || []}
                    getOptionLabel={(option) => option?.title || ""}
                    value={values.Relation}
                    onChange={(_, newValue) => setFieldValue("Relation", newValue)}
                    renderInput={(params) => (
                      <TextField {...params} label="Relation *" variant="standard"
                        error={touched.Relation && Boolean(errors.Relation)}
                        helperText={touched.Relation && errors.Relation?.title}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="MobileNo"
                    label="Mobile Number"
                    variant="standard"
                    value={values.MobileNo}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.MobileNo && Boolean(errors.MobileNo)}
                    helperText={touched.MobileNo && errors.MobileNo}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl component="fieldset" variant="standard">
                    <FormLabel component="legend" sx={{ fontSize: '0.8rem' }}>Gender</FormLabel>
                    <RadioGroup row name="Gender" value={values.Gender} onChange={handleChange}>
                      <FormControlLabel value="Male" control={<Radio size="small" />} label="Male" />
                      <FormControlLabel value="Female" control={<Radio size="small" />} label="Female" />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DateField
                      fullWidth
                      label="DOB (Record)"
                      variant="standard"
                      format="dd-MM-yyyy"
                      value={values.DOBR}
                      onChange={(val) => {
                        setFieldValue("DOBR", val);
                        setFieldValue("DOBG", val);
                      }}
                      onBlur={() => setFieldValue("DOBR", values.DOBR, true)}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DateField
                      fullWidth
                      label="DOB (Greeting)"
                      variant="standard"
                      format="dd-MM-yyyy"
                      value={values.DOBG}
                      onChange={(val) => setFieldValue("DOBG", val)}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DateField
                      fullWidth
                      label="Date of Marriage"
                      variant="standard"
                      format="dd-MM-yyyy"
                      value={values.DOM}
                      onChange={(val) => setFieldValue("DOM", val)}
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    name="MaritalStatus"
                    options={[
                      { title: "Single", value: "Single" },
                      { title: "Married", value: "Married" },
                      { title: "Divorced", value: "Divorced" },
                      { title: "Widowed", value: "Widowed" },
                    ]}
                    getOptionLabel={(option) => option?.title || ""}
                    value={values.MaritalStatus}
                    onChange={(_, newValue) => setFieldValue("MaritalStatus", newValue)}
                    renderInput={(params) => <TextField {...params} label="Marital Status" variant="standard" />}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="Education"
                    label="Education"
                    variant="standard"
                    value={values.Education}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    name="Work"
                    options={ProfessionList.data || []}
                    getOptionLabel={(option) => option?.title || ""}
                    value={values.Work}
                    onChange={(_, newValue) => setFieldValue("Work", newValue)}
                    renderInput={(params) => <TextField {...params} label="Profession" variant="standard" />}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    name="WorkType"
                    options={WorkTypeList.data || []}
                    getOptionLabel={(option) => option?.title || ""}
                    value={values.WorkType}
                    onChange={(_, newValue) => setFieldValue("WorkType", newValue)}
                    renderInput={(params) => <TextField {...params} label="Work Type" variant="standard" />}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="WorkPlace"
                    label="Workplace"
                    variant="standard"
                    value={values.WorkPlace}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="OldLIC"
                    label="Old LIC Policies"
                    variant="standard"
                    value={values.OldLIC}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Stack direction="row" justifyContent="flex-end" spacing={2}>
                    <LoadingButton
                      variant="contained"
                      color="primary"
                      type="submit"
                      loading={isSubmitting}
                      disabled={isSubmitting || !referenceId}
                    >
                      Save Family Member
                    </LoadingButton>
                  </Stack>
                </Grid>
              </Grid>
            </form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
